import React, { useState } from "react";
import { Card, Tooltip, Modal } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DISPLAY_FORMAT, parseDate, formatHotelPortalCancellationDisplayDate, applyHotelPortalCancellationDisplayOffset } from "../../../helpers/dateHelper";
import dayjs from "dayjs";
import { useCurrencyContext } from "../../../common/providers/CurrencyProvider";
import {
  getHotelRatePlanFreeCancelDate,
  formatHotelCancellationPenaltyDisplay,
  isSyntheticHotelCancellationPolicy,
  getHotelNonRefundableCheckoutPolicies,
  formatHotelApiCancellationPolicyDate,
  resolveHotelCancellationPenaltyBasisTotal,
  getHotelNonRefundableCheckoutTablePolicies,
  isHotelRatePlanNonRefundableForCheckout,
} from "../hotelCancellation";
import { getRoomMealCategoryLabel } from "../hotelMealTypes";
import HotelCardImage from "./HotelCardImage";
import ImagesLightbox from "../../../components/ImagesLightbox/ImagesLightbox";
import { getHotelCurrencySymbol } from "../hotelCurrency";
import { getHotelRoomDisplayTitle } from "../hotelRoomDisplay";

const RoomDetailsCard = ({ RoomDetails }) => {
    const {
        activeCurrency: selectedCurrency,
        currencyValue,
    } = useCurrencyContext();
    const activeCurrency = getHotelCurrencySymbol(selectedCurrency);
    const [imagesModal, setImagesModal] = useState({
        visible: false,
        roomName: "",
        images: [],
        startIndex: 0,
    });

    const cancellationTableStyle = {
        border: "1px solid #fff"
    }

    const formatPolicyDate = (date) => {
        if (!date) return "";
        const formatted = formatHotelPortalCancellationDisplayDate(date, DISPLAY_FORMAT);
        if (formatted) return formatted;
        return String(date);
    };

    const formatPenalty = (penaltyAmount, roomTotal) =>
        formatHotelCancellationPenaltyDisplay(penaltyAmount, roomTotal, {
            freeLabel: "Free",
        });

    const formatPenaltyForTable = (penaltyAmount, roomTotal, ratePlan) => {
        if (isNonRefundableRatePlan(ratePlan)) {
            const amount = parseFloat(String(penaltyAmount ?? "").trim());
            if (!Number.isNaN(amount) && amount > 100) {
                const currency =
                    ratePlan?.price?.currency ??
                    ratePlan?.price?.Currency ??
                    "INR";
                return `${currency} ${amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            }
        }
        return formatPenalty(penaltyAmount, roomTotal);
    };

    const isSyntheticPolicy = isSyntheticHotelCancellationPolicy;

    const sortCancellationPolicies = (policies = []) =>
        [...policies].filter((p) => !isSyntheticPolicy(p)).sort((a, b) => {
            const da = parseDate(a?.fromDate);
            const db = parseDate(b?.fromDate);
            if (da?.isValid?.() && db?.isValid?.()) {
                return da.valueOf() - db.valueOf();
            }
            return 0;
        });

    const isNonRefundableRatePlan = (ratePlan) =>
        isHotelRatePlanNonRefundableForCheckout(ratePlan);

    const getPoliciesForCancellationTable = (ratePlan) => {
        if (isNonRefundableRatePlan(ratePlan)) {
            return getHotelNonRefundableCheckoutTablePolicies(ratePlan);
        }
        const real = sortCancellationPolicies(ratePlan?.cancellationPolicy ?? []);
        if (real.length > 0) return real;
        // FC selection with deadline but slabs not enriched yet — one Free row (matches green date).
        const freeRaw =
            getHotelRatePlanFreeCancelDate(ratePlan) ||
            ratePlan?.lastCancellationDate ||
            ratePlan?.LastCancellationDate ||
            ratePlan?.lastVoucherDate ||
            "";
        if (!freeRaw) return [];
        const toDate = dayjs.isDayjs(freeRaw)
            ? freeRaw.toISOString()
            : String(freeRaw);
        return [
            {
                fromDate: dayjs().startOf("day").toISOString(),
                toDate,
                penaltyAmount: 0,
            },
        ];
    };

    const formatPolicyDateForTable = (ratePlan, date) => {
        if (isNonRefundableRatePlan(ratePlan)) {
            const formatted = formatHotelApiCancellationPolicyDate(date);
            return formatted || String(date ?? "");
        }
        return formatPolicyDate(date);
    };

    const cancellationPolicyTable = (ratePlan) => {
        const policies = getPoliciesForCancellationTable(ratePlan);
        const roomTotal = resolveHotelCancellationPenaltyBasisTotal(ratePlan);
        if (policies.length === 0 && isNonRefundableRatePlan(ratePlan)) {
            return (
                <p className="mb-0 px-2 py-1">
                    Non-refundable — full room charges apply on cancellation.
                </p>
            );
        }
        return (
            <table className="cancellationDetails text-center" style={{ minWidth: "500px", width: "100%" }}>
                <thead>
                    <tr>
                        <th style={cancellationTableStyle}>Cancelled on or After</th>
                        <th style={cancellationTableStyle}>Cancelled on or Before</th>
                        <th style={cancellationTableStyle}>Cancellation Charges</th>
                    </tr>
                </thead>
                <tbody>
                    {policies.length > 0 && policies.map((cancellationDate, i) => {
                        return (
                            <tr key={i} className={`cancellationDate${i}`} style={cancellationTableStyle}>
                                <td style={cancellationTableStyle}>{formatPolicyDateForTable(ratePlan, cancellationDate?.fromDate)}</td>
                                <td style={cancellationTableStyle}>{formatPolicyDateForTable(ratePlan, cancellationDate?.toDate)}</td>
                                <td style={cancellationTableStyle}>{formatPenaltyForTable(cancellationDate?.penaltyAmount, roomTotal, ratePlan)}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    };

    const getAllInclusions = (inclusions) => {
        let inclusion = inclusions.split(",");
        if (inclusion.length > 2) {
            return (
                <Tooltip 
                className="pe-auto"
                    title={inclusion.map((i, key) => (
                        <p key={key} className="mb-0">
                            {i}
                        </p>
                    ))}
                >
                    <a className="ml-auto text-info pe-auto">Show More</a>
                </Tooltip>
            );
        }
    }

    const getFreeCancellationDisplay = (ratePlan) => {
        if (isHotelRatePlanNonRefundableForCheckout(ratePlan)) {
            return { type: "non-refundable" };
        }
        const raw = getHotelRatePlanFreeCancelDate(ratePlan);
        if (!raw) {
            // Refundable without a free-cancel deadline — do not show green FC line.
            return null;
        }
        const shifted = applyHotelPortalCancellationDisplayOffset(
            dayjs.isDayjs(raw) ? raw.format("YYYY-MM-DD") : raw,
        );
        if (!shifted?.isValid?.() || shifted.isBefore(dayjs().startOf("day"))) {
            return null;
        }
        return {
            type: "free",
            label: formatHotelPortalCancellationDisplayDate(raw, DISPLAY_FORMAT) ||
                shifted.format(DISPLAY_FORMAT),
        };
    };

    const onHandleModal = (payload) => {
        setImagesModal({
            visible: true,
            roomName: payload?.roomName || "Room images",
            images: Array.isArray(payload?.images) ? payload.images : [],
            startIndex: Number(payload?._startImageIndex) || 0,
        });
    };

    const hasHotelImages =
        RoomDetails.hasOwnProperty("images") &&
        Array.isArray(RoomDetails?.images) &&
        RoomDetails.images.length > 0;

    return (
        <Card>
            {RoomDetails.rooms && RoomDetails.rooms.length > 0 ? (
                RoomDetails.rooms.map((room, i) => (
                    <React.Fragment key={i}>
                        <h6 className="text-danger"><b>Room {i + 1}</b></h6>
                        <div className="room-main-section room-details-card-border d-lg-flex w-100 justify-content-start p-3 my-3" key={i}>
                            {hasHotelImages ? (
                                <div
                                  className="room-image my-1 w-md-100 checkout-room-image-carousel"
                                  style={{ maxWidth: "150px" }}
                                >
                                  <HotelCardImage
                                    hotelDetailsRespObj={RoomDetails}
                                    hotelRoom={room}
                                    imageOffset={i * 3}
                                    autoplay
                                    autoplaySpeed={2000}
                                    onHandleModal={onHandleModal}
                                  />
                                </div>
                            ) : (
                                <div className="not-found" style={{ width: "150px", height: "125px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
                                    <img src={require("../../../assets/images/hotels/no_img.png")} alt="No Image" height={100} width={100} />
                                </div>
                            )}
                            <div className="room-wrap d-block w-100 mx-0 px-3">
                                <div className="room-details m-0 d-block">
                                    <div className="room-title">
                                        <p className="roomName font-weight-bold">{getHotelRoomDisplayTitle(room, RoomDetails?.hotelName)}</p>
                                        {(() => {
                                            const mealCategory = getRoomMealCategoryLabel(
                                                room,
                                                room?.ratePlans?.[0],
                                            );
                                            if (!mealCategory) return null;
                                            return (
                                                <p
                                                    className="room-meal-category mb-0"
                                                    style={{
                                                        color: "#5c6b8a",
                                                        fontWeight: 500,
                                                        fontSize: 14,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {mealCategory}
                                                </p>
                                            );
                                        })()}
                                    </div>
                                    {(() => {
                                        const cancelInfo = getFreeCancellationDisplay(room.ratePlans?.[0]);
                                        if (!cancelInfo) return null;
                                        if (cancelInfo.type === "non-refundable") {
                                            return (
                                                <div className="cancelation-policy text-danger">
                                                    <p className="roomName font-weight-bold">Non-refundable</p>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div className="cancelation-policy text-success">
                                                <p className="roomName font-weight-bold">
                                                    Free Cancellation Till : {cancelInfo.label}
                                                </p>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {room.ratePlans.map((ratePlans, i) => (
                                    <React.Fragment>
                                        {ratePlans.amenities && ratePlans.amenities.length > 0 && (
                                            <div className="inclusion m-0 w-100 d-flex">
                                                <p className="roomName font-weight-bold">Inclusions :
                                                    {ratePlans.amenities.length > 0 ? ratePlans.amenities[0].split(",").map((x, key) => key < 2 ? (
                                                        <span>&nbsp; <i className="fa fa-check color-blue" aria-hidden="true"></i> {x}</span>
                                                    ) : null) : <span>Not Available</span>}
                                                </p>
                                                {ratePlans.amenities.length > 0 && getAllInclusions(ratePlans.amenities[0])}
                                            </div>
                                        )}

                                        <div className="cancelation-foolter d-flex justify-content-between">
                                            <div className="inclusion m-0">
                                                <p className="roomName font-weight-bold m-0">
                                                    Cancellation Policy{" "}
                                                    <span>
                                                        <Tooltip
                                                            overlayStyle={{ maxWidth: "600px" }}
                                                            title={cancellationPolicyTable(ratePlans)}
                                                        >
                                                            <InfoCircleOutlined />
                                                        </Tooltip>
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="room-price ml-auto">
                                                <p className="roomName font-weight-bold">{activeCurrency} {currencyValue(room.ratePlans[0].price.total)}</p>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}

                            </div>
                        </div>
                    </React.Fragment>
                ))
            ) : null}

            <Modal
              wrapClassName="modalHeader hotelImgModal checkout-hotel-img-modal"
              open={imagesModal.visible}
              visible={imagesModal.visible}
              title={imagesModal.roomName || "Room images"}
              onCancel={() =>
                setImagesModal({
                  visible: false,
                  roomName: "",
                  images: [],
                  startIndex: 0,
                })
              }
              footer={null}
              destroyOnClose
              centered
              width={920}
            >
              {imagesModal.visible ? (
                <ImagesLightbox
                  hotelImages={imagesModal.images}
                  selectedItem={Number(imagesModal.startIndex) || 0}
                  centerMode={false}
                />
              ) : null}
            </Modal>
        </Card >
    )
}

export default RoomDetailsCard;
